import { jwtDecode } from 'jwt-decode';
import { AUTH_USE_HTTPONLY_COOKIE } from '../config/authFlags';

const TOKEN_KEY = 'jwt';
const LEGACY_KEYS = ['token', 'authToken', 'accessToken', 'userToken'];
/** Purane build se bacha ho to hata do — JWT ab cookie mode mein sessionStorage mein nahi rakhte. */
const LEGACY_COOKIE_MODE_BEARER_KEY = '__osis_cookie_mode_bearer';
/** Non-secret mirror of `userInfo.id` for UI / guards when JWT is HttpOnly-only (not in JS). */
const CLAIMS_SNAPSHOT_KEY = '__osis_claims_snapshot';

let memoryToken = null;

function getSessionToken() {
  if (AUTH_USE_HTTPONLY_COOKIE) return null;
  try {
    const v = sessionStorage.getItem(TOKEN_KEY);
    if (v && v.trim() !== '' && v !== 'null') return v.trim();
    for (const k of LEGACY_KEYS) {
      const v2 = sessionStorage.getItem(k);
      if (v2 && v2.trim() !== '' && v2 !== 'null') return v2.trim();
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

/**
 * Raw JWT string: non–cookie mode mein sessionStorage; cookie mode mein **sirf memory** (same tab) —
 * HttpOnly session cookie JS se read nahi hoti; JWT ko sessionStorage / localStorage mein persist nahi karte.
 */
export function getToken() {
  if (AUTH_USE_HTTPONLY_COOKIE) {
    return memoryToken || null;
  }
  return memoryToken || getSessionToken();
}

export function getUserClaimsSnapshot() {
  if (!AUTH_USE_HTTPONLY_COOKIE) return null;
  try {
    const raw = sessionStorage.getItem(CLAIMS_SNAPSHOT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setUserClaimsSnapshot(userInfo) {
  if (!AUTH_USE_HTTPONLY_COOKIE || !userInfo?.id || typeof userInfo.id !== 'object') return;
  try {
    sessionStorage.setItem(CLAIMS_SNAPSHOT_KEY, JSON.stringify(userInfo.id));
  } catch {
    /* ignore */
  }
}

export function clearUserClaimsSnapshot() {
  try {
    sessionStorage.removeItem(CLAIMS_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * True when this tab can call protected APIs.
 * Cookie mode: snapshot is set only after server `/api/auth/me` success (not for replayed login JSON).
 */
export function hasAuthSession() {
  if (!AUTH_USE_HTTPONLY_COOKIE) {
    return Boolean(getToken());
  }
  return Boolean(getToken() || getUserClaimsSnapshot());
}

/**
 * Same shape as `jwtDecode` for UI: `{ id: { …claims } }`.
 * Cookie mode: **prefer `/api/auth/me` snapshot** (ROLE_CODE, USER_TYPE, …) over Bearer JWT decode.
 */
export function getUserClaimsForUi() {
  if (AUTH_USE_HTTPONLY_COOKIE) {
    const snap = getUserClaimsSnapshot();
    if (snap && typeof snap === 'object') return { id: snap };
  }
  const t = getToken();
  if (t && String(t).trim() && t !== 'null') {
    try {
      return jwtDecode(t);
    } catch {
      /* ignore */
    }
  }
  const snap = getUserClaimsSnapshot();
  if (snap && typeof snap === 'object') return { id: snap };
  return null;
}

/** Flat claims for `ROLE_CODE` / `USER_TYPE` checks (jwt `id` == `/api/auth/me` user). */
export function getClaimsForRoleUi() {
  const wrapped = getUserClaimsForUi();
  return wrapped?.id ?? null;
}

/**
 * HttpOnly cookie mode: JWT **sessionStorage mein mat rakho** — sirf optional in-memory Bearer (signin body se)
 * isi tab ke liye. Auth source: **HttpOnly cookie** + `withCredentials`, Bearer tab fallback jab cookie cross-origin na bheje.
 * Legacy (non–cookie): JWT sessionStorage.
 */
export function setToken(token) {
  if (AUTH_USE_HTTPONLY_COOKIE) {
    if (typeof token === 'string' && token.trim() !== '') {
      memoryToken = token
        .trim()
        .replace(/^Bearer\s+/i, '')
        .replace(/^['"]|['"]$/g, '');
    } else {
      memoryToken = null;
    }
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(LEGACY_COOKIE_MODE_BEARER_KEY);
      LEGACY_KEYS.forEach((k) => {
        sessionStorage.removeItem(k);
        localStorage.removeItem(k);
      });
      localStorage.removeItem(TOKEN_KEY);
    } catch (_) {
      /* ignore */
    }
    return;
  }

  memoryToken = null;
  if (typeof token === 'string' && token.trim() !== '') {
    memoryToken = token.trim();
  }

  if (!token || typeof token !== 'string') return;
  const trimmed = token.trim();
  try {
    sessionStorage.setItem(TOKEN_KEY, trimmed);
    LEGACY_KEYS.forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
    localStorage.removeItem(TOKEN_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function purgeStoredJwtIfCookieMode() {
  if (!AUTH_USE_HTTPONLY_COOKIE) return;
  /** Do NOT clear claims snapshot — it holds `/api/auth/me` user for ROLE/UI after hard refresh. */
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(LEGACY_COOKIE_MODE_BEARER_KEY);
    LEGACY_KEYS.forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
    localStorage.removeItem(TOKEN_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function clearAllStorage() {
  memoryToken = null;
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (_) {
    /* ignore */
  }
}

export function getAuthHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  const token = getToken();
  const clean = token ? String(token).replace(/['"]/g, '').trim() : null;
  return {
    withCredentials: true,
    headers: {
      ...headers,
      ...(clean && { Authorization: `Bearer ${clean}` }),
    },
  };
}
